/**
 * Группы клавиш и порядок их открытия.
 *
 * Порядок один и тот же для любой раскладки, потому что он про руки, а не
 * про язык: сначала домашний ряд, где стоят пальцы, затем верхний и нижний;
 * внутри ряда — от указательных пальцев к мизинцам, от самых сильных к самым
 * слабым (ТЗ FR-EN-11, §6 режим 2).
 *
 * Состав группы берётся из раскладки, а не из списка в коде: список пришлось
 * бы править при каждой новой раскладке, и он разошёлся бы с подсказкой
 * пальца.
 */

import type { MasteryUnit } from '@type2x/shared-contracts';
import { toMasteryUnitId } from '@type2x/shared-contracts';
import { fingerPairOf, getLayout, type KeyDefinition, type KeyRow } from '@type2x/keyboard-layouts';
import { characterFrequency } from '@type2x/corpora';
import {
  currentUnlockableGroup,
  masteredGroups,
  unlockedGroups,
  type UnlockableGroup,
} from '@type2x/learning-engine';

import type { FingerPair, FundamentalsSnapshot, KeyGroup } from '../contracts/fundamentals.js';
import { FINGER_PAIRS, FUNDAMENTAL_ROWS } from '../contracts/fundamentals.js';

/** Группа в виде, понятном общему механизму открытия. */
function unlockable(group: KeyGroup): UnlockableGroup {
  return {
    id: group.id,
    unitIds: group.characters.map((character) => toMasteryUnitId(`key:${character}`)),
  };
}

/** Учебная единица клавиши. */
export function keyUnit(snapshot: FundamentalsSnapshot, character: string): MasteryUnit {
  return {
    id: toMasteryUnitId(`key:${character}`),
    kind: 'key',
    content: character,
    language: snapshot.language,
    layoutId: snapshot.layoutId,
    realWorldFrequency: characterFrequency(snapshot.language, character),
  };
}

/**
 * Буква ли это.
 *
 * «Основы» учат буквам: цифры, знаки препинания и специальные символы —
 * отдельные режимы (ТЗ §6, режимы 8–10), и смешивать их здесь значит учить
 * новичка всему сразу.
 */
function isLetter(character: string): boolean {
  return /^\p{L}$/u.test(character);
}

/** Клавиши ряда, относящиеся к паре пальцев, слева направо. */
function keysOf(keys: readonly KeyDefinition[], row: KeyRow, pair: FingerPair): KeyDefinition[] {
  return keys
    .filter((key) => key.row === row && fingerPairOf(key.finger) === pair && isLetter(key.base))
    .sort((left, right) => left.column - right.column);
}

/** Все группы раскладки в порядке открытия. */
export function keyGroups(snapshot: FundamentalsSnapshot): KeyGroup[] {
  const { keys } = getLayout(snapshot.layoutId);
  const groups: KeyGroup[] = [];

  for (const row of FUNDAMENTAL_ROWS) {
    for (const pair of FINGER_PAIRS) {
      const characters = keysOf(keys, row, pair).map((key) => key.base);
      // Пустая группа означает, что в этом ряду у этой пары пальцев букв нет:
      // показывать пользователю пустой шаг незачем.
      if (characters.length > 0)
        groups.push({ id: `${row}-${pair}`, row, fingerPair: pair, characters });
    }
  }

  return groups;
}

/** Группы, пройденные к этому моменту. */
export function completedGroups(snapshot: FundamentalsSnapshot): KeyGroup[] {
  const groups = keyGroups(snapshot);
  const mastered = new Set(masteredGroups(groups.map(unlockable), snapshot.stats).map((g) => g.id));
  return groups.filter((group) => mastered.has(group.id));
}

/**
 * Группа, которую пользователь осваивает сейчас.
 *
 * Это первая непройденная группа: пропускать вперёд нельзя — следующий ряд
 * ставится от домашнего, и без него движение пальца не с чего отмерять.
 * Пройдены все — режиму больше нечему учить, возвращается null.
 */
export function currentGroup(snapshot: FundamentalsSnapshot): KeyGroup | null {
  const groups = keyGroups(snapshot);
  const current = currentUnlockableGroup(groups.map(unlockable), snapshot.stats);
  return groups.find((group) => group.id === current?.id) ?? null;
}

/** Символы, доступные упражнению: пройденные группы и текущая. */
export function unlockedCharacters(snapshot: FundamentalsSnapshot): string[] {
  const groups = keyGroups(snapshot);
  const open = new Set(unlockedGroups(groups.map(unlockable), snapshot.stats).map((g) => g.id));

  return groups.filter((group) => open.has(group.id)).flatMap((group) => group.characters);
}

/** Есть ли режиму чему учить: остались непройденные группы. */
export function hasFundamentals(snapshot: FundamentalsSnapshot): boolean {
  return currentGroup(snapshot) !== null;
}
